import React, { createContext, useEffect, useState } from 'react';
import { ClientSocket } from '../services/ClientSocket';
import { LocalStorage } from '../services/LocalStorage';
import { EquationsService } from '../services/equations/equationService';
import { StatusCodes } from 'http-status-codes';
import { toast } from 'react-toastify';
import { setStyle } from '../util';

interface Props {
    children: React.ReactNode
}


export interface IAppContext {
    jwtToken: string | null,
    role: string | null,
    userId: string | null,
    hasReadConsent:boolean,
    needResume:boolean,
    store:string
    server: string | null,
    socket: ClientSocket | null,
    isSocketConnected: boolean,
    defaultValues: any,
    survey:any|undefined,
    setJwtToken: React.Dispatch<React.SetStateAction<string | null>>,
    setRole: React.Dispatch<React.SetStateAction<string | null>>,
    setUserId: React.Dispatch<React.SetStateAction<string | null>>,
    setReadConsent: React.Dispatch<React.SetStateAction<boolean>>,
    setNeedResume:React.Dispatch<React.SetStateAction<boolean>>,
    setStore: React.Dispatch<React.SetStateAction<string>>,
    setSurvey: React.Dispatch<React.SetStateAction<any>>,
    setServer: React.Dispatch<React.SetStateAction<string | null>>,
    setSocket: React.Dispatch<React.SetStateAction<ClientSocket | null>>,
    setIsSocketConnected: React.Dispatch<React.SetStateAction<boolean>>,
    setDefaultValues: React.Dispatch<React.SetStateAction<any>>,
    connectSocket: () => Promise<void>,
    disconnectSocket: () => void,
    logout: () => void
}

export const AppContext = createContext<IAppContext>(
    {
        jwtToken: LocalStorage.getJwtToken(),
        role: "",
        userId: null,
        hasReadConsent:false,
        needResume:false,
        server: "ALPHA",
        socket: null,
        store:"",
        isSocketConnected: false,
        defaultValues: null,
        survey:undefined,
        setJwtToken: () => { /* *Does nothing.* */ },
        setRole: () => { /* *Does nothing.* */ },
        setStore:()=>{},
        setUserId: () => { /* *Does nothing.* */ },
        setServer: () => { /* *Does nothing.* */ },
        setSocket: () => { /* *Does nothing.* */ },
        setIsSocketConnected: () => { /* *Does nothing.* */ },
        setDefaultValues: () => { /* *Does nothing.* */ },
        connectSocket: async () => { /* *Does nothing.* */ },
        disconnectSocket: () => { /* *Does nothing.* */ },
        logout: () => { /* *Does nothing.* */ },
        setReadConsent: ()=>{/* *Does nothing.* */},
        setNeedResume:()=>{/* *Does nothing.* */},
        setSurvey: ()=>{/* *Does nothing.* */}
    }
);


export const AppContextProvider = ({ children }: Props) => {
    const [token, setToken] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [store, setStore] = useState<string>("");
    const [userId, setUserId] = useState<string | null>(null);
    const [server, setServer] = useState<string | null>("ALPHA");
    const [socket, setSocket] = useState<ClientSocket | null>(null);
    const [isSocketConnected, setIsSocketConnected] = useState<boolean>(false);
    const [defaultValues, setDefaultValues] = useState<any>(null);
    const [hasReadConsent, setReadConsent] = useState<boolean>(false)
    const [needResume, setNeedResume] = useState<boolean>(false)
    const [survey, setSurvey] = useState<any>(undefined)

    const connectSocket = async (): Promise<void> => {
        if (!socket) {
            console.error('[AppContext] No socket instance available for connection');
            return;
        }

        try {
            await socket.connect();
            setIsSocketConnected(true);
            console.log('[AppContext] Socket connected successfully');
        } catch (error) {
            console.error('[AppContext] Socket connection failed:', error);
            setIsSocketConnected(false);
            throw error;
        }
    };

    const disconnectSocket = (): void => {
        if (socket && socket.isConnected) {
            socket.disconnect();
            console.log('[AppContext] Socket disconnected');
        }
        setIsSocketConnected(false);
    };

    const logout = (): void => {
        console.log('[AppContext] Logging out user');
        
        // Disconnect socket
        disconnectSocket();
        
        // Clear all authentication data
        setToken(null);
        setRole(null);
        setUserId(null);
        setSocket(null);
        
        // Clear localStorage
        LocalStorage.clearAll();
        
        console.log('[AppContext] User logged out successfully');
    };

    // Initialize socket and connect immediately when context loads
    useEffect(() => {
        console.log('[AppContext] 🚀 Initializing WebSocket connection on app startup...');
        
        // Create socket instance immediately
        const clientSocket = new ClientSocket();
        setSocket(clientSocket);
        
        // Connect socket immediately
        const connectImmediately = async () => {
            try {
                console.log('[AppContext] 🔌 Connecting WebSocket...');
                await clientSocket.connect();
                setIsSocketConnected(true);
                console.log('[AppContext] ✅ WebSocket connected successfully on app startup');
            } catch (error) {
                console.error('[AppContext] ❌ Failed to connect WebSocket on startup:', error);
                setIsSocketConnected(false);
                // Don't throw - continue app initialization even if socket fails
            }
        };
        
        connectImmediately();
        
        // Also fetch default values
        const fetchData = async () => {
            const res = await EquationsService.getGlobalDefaultValues();
            if (res.status === StatusCodes.OK) {
                setDefaultValues(res.data);
                setStyle(res.data)
            } else {
                toast("Erreur de communication avec le serveur.");
            }
        }

        fetchData();
    }, []);

    // Clean up socket connection when context is destroyed
    useEffect(() => {
        return () => {
            disconnectSocket();
        };
    }, []);

    // Handle browser close/refresh - disconnect socket gracefully
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (socket && socket.isConnected) {
                console.log('[AppContext] Browser closing - disconnecting socket');
                socket.disconnect();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [socket]);

    return (
        <AppContext.Provider value={{
            store:store,setStore:setStore,
            jwtToken: token, setJwtToken: setToken, role: role, setRole: setRole, userId: userId, setUserId: setUserId,
            hasReadConsent:hasReadConsent,
            needResume:needResume,
            survey:survey,
            server: server, setServer: setServer, socket: socket, setSocket: setSocket, 
            isSocketConnected: isSocketConnected, setIsSocketConnected: setIsSocketConnected,
            defaultValues: defaultValues, setDefaultValues: setDefaultValues,
            setReadConsent:setReadConsent,
            setNeedResume:setNeedResume,
            setSurvey:setSurvey,
            connectSocket: connectSocket, disconnectSocket: disconnectSocket, logout: logout
        }}>
            {children}
        </AppContext.Provider>
    );
}
